import type React from "react";
import {
  CheckCircle2,
  Clock,
  CreditCard,
  Link2,
  Package,
  RotateCcw,
  Truck,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { SaleEvent } from "@/hooks/useSaleHistory";

const TYPE_CONFIG: Record<string, { 
  icon: React.ElementType; 
  bgColor: string; 
  iconColor: string;
  borderColor: string;
}> = {
  checkout_link_sent: { 
    icon: Link2, 
    bgColor: "bg-blue-100 dark:bg-blue-900/30", 
    iconColor: "text-blue-600 dark:text-blue-400",
    borderColor: "border-blue-300 dark:border-blue-700"
  },
  payment_confirmed: { 
    icon: CreditCard, 
    bgColor: "bg-green-100 dark:bg-green-900/30", 
    iconColor: "text-green-600 dark:text-green-400",
    borderColor: "border-green-300 dark:border-green-700"
  },
  fulfillment_pending: { 
    icon: Clock, 
    bgColor: "bg-amber-100 dark:bg-amber-900/30", 
    iconColor: "text-amber-600 dark:text-amber-400",
    borderColor: "border-amber-300 dark:border-amber-700"
  },
  fulfillment_ready: { 
    icon: Package, 
    bgColor: "bg-purple-100 dark:bg-purple-900/30", 
    iconColor: "text-purple-600 dark:text-purple-400",
    borderColor: "border-purple-300 dark:border-purple-700"
  },
  dispatched: { 
    icon: Truck, 
    bgColor: "bg-indigo-100 dark:bg-indigo-900/30", 
    iconColor: "text-indigo-600 dark:text-indigo-400",
    borderColor: "border-indigo-300 dark:border-indigo-700"
  },
  delivered: { 
    icon: CheckCircle2, 
    bgColor: "bg-emerald-100 dark:bg-emerald-900/30", 
    iconColor: "text-emerald-600 dark:text-emerald-400",
    borderColor: "border-emerald-300 dark:border-emerald-700"
  },
  concluded: { 
    icon: CheckCircle2, 
    bgColor: "bg-emerald-100 dark:bg-emerald-900/30", 
    iconColor: "text-emerald-600 dark:text-emerald-400",
    borderColor: "border-emerald-300 dark:border-emerald-700"
  },
  checkout_reactivated: {
    icon: RotateCcw,
    bgColor: "bg-sky-100 dark:bg-sky-900/30",
    iconColor: "text-sky-600 dark:text-sky-400",
    borderColor: "border-sky-300 dark:border-sky-700",
  },
  checkout_expired: {
    icon: Clock,
    bgColor: "bg-orange-100 dark:bg-orange-900/30",
    iconColor: "text-orange-600 dark:text-orange-400",
    borderColor: "border-orange-300 dark:border-orange-700",
  },
  cancelled: {
    icon: Clock,
    bgColor: "bg-rose-100 dark:bg-rose-900/30",
    iconColor: "text-rose-600 dark:text-rose-400",
    borderColor: "border-rose-300 dark:border-rose-700",
  },
};

const DEFAULT_CONFIG = {
  icon: Clock,
  bgColor: "bg-muted",
  iconColor: "text-muted-foreground",
  borderColor: "border-border"
};

function formatDateTime(dateString: string) {
  return new Date(dateString).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function SaleTimeline({
  events,
  isLoading,
}: {
  events: SaleEvent[];
  isLoading?: boolean;
}) {
  // Check if order is concluded
  const isOrderConcluded = events.some(
    (evt) => evt.event_type === "concluded" || evt.event_type === "delivered"
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Histórico
        </h4>
        {isOrderConcluded && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 px-3 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Pedido Concluído
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          Carregando histórico…
        </div>
      ) : events.length === 0 ? (
        <div className="flex items-center justify-center py-6 text-sm text-muted-foreground border border-dashed rounded-lg">
          <Clock className="h-4 w-4 mr-2" />
          Sem eventos ainda
        </div>
      ) : (
        <ol className="relative space-y-4 border-l-2 border-muted pl-6 ml-2">
          {events.map((evt) => {
            const config = TYPE_CONFIG[evt.event_type] ?? DEFAULT_CONFIG;
            const Icon = config.icon;
            
            return (
              <li key={evt.id} className="relative">
                <span
                  className={cn(
                    "absolute -left-[1.625rem] top-0.5 flex h-6 w-6 items-center justify-center rounded-full border-2",
                    config.bgColor,
                    config.borderColor
                  )}
                >
                  <Icon className={cn("h-3.5 w-3.5", config.iconColor)} />
                </span>
                <div className="space-y-1">
                  <div className="text-sm font-medium leading-tight">
                    {evt.message}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatDateTime(evt.created_at)}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
